import commonMessages from '@rhg/lang/messages/common';
import { Box, Stack, SxProps, Theme, Typography } from '@rhg/libs/material';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { AudioPlayerControls } from '../types';

/**
 * Shape matches SDK MediaNarrator/MediaAuthor so callers can pass those
 * objects through directly. A narrator with `isComposer: true` renders as
 * an Artist credit (and never as a Narrator).
 */
export interface AudioPlayerCredit {
    name?: string;
    isComposer?: boolean;
}

export type CreditLabelKey =
    | 'common.narrator'
    | 'common.author'
    | 'common.artist'
    | 'common.narrator_and_author'
    | 'common.artist_and_author';

interface CreditBlock {
    labelKey: CreditLabelKey;
    names: string[];
}

/** Which contributor role a {@link CreditGroup} represents. */
export type CreditRole = 'narrator' | 'author';

/**
 * A labeled credit group carrying the source contributor object(s).
 * Generic so callers can pass richer objects (e.g. `MediaNarrator`) and
 * receive them back in `members` without losing extra fields.
 */
export interface CreditGroup<T extends AudioPlayerCredit = AudioPlayerCredit> {
    labelKey: CreditLabelKey;
    /** Which contributor this group represents (for avatar / dialog). */
    role: CreditRole;
    /** The source object(s); single-entity inputs produce exactly one member. */
    members: T[];
}

/**
 * Decides which labeled credit groups to show given the player variant,
 * carrying the source contributor object(s) so callers have access to
 * avatars and other rich fields.
 *
 * Single-entity-per-role: callers pass at most one narrator and one author.
 * A narrator with `isComposer: true` is partitioned up front and treated as
 * an Artist (the same person never appears as both Narrator and Artist).
 *
 * Combo logic mirrors the iOS and Android implementations to keep cross-
 * platform labeling consistent. See:
 *   - iOS: evexia-ios/Evexia/Features/MediaPlayer/Utils/MediaPlayerLibraryRepository.swift:240-246
 *   - Android: evexia-android/app/src/main/java/com/ripplehealthgroup/evexia/features/player/audio/AudioPlayerViewModel.kt:1829-1867
 *
 * Cases (strict name equality):
 *   - narrator == author == artist: emit three separate blocks. Web lacks a
 *     `narrator_author_and_artist` label, so we fall back to three blocks
 *     instead of one combined block (see follow-up note in code review).
 *   - narrator == author, distinct artist: NarratorAndAuthor + Artist.
 *   - artist == author, distinct narrator: ArtistAndAuthor + Narrator.
 *   - narrator == artist, distinct author: Artist only for that person
 *     (Android drops the narrator label here — line 1853) + Author. Web
 *     lacks a `narrator_and_artist` label, so the Artist label stands in.
 *   - else: separate Narrator + Author + Artist blocks.
 *
 * Per-variant rules:
 *   - Regular (talk/clinical): combines Narrator+Author when equal; never
 *     shows Artist (a composer narrator is dropped entirely).
 *   - Playlist / MusicAlbum / MusicSingle (music, any track count): full
 *     combo logic above — billing is a content-type concern, not a nav-style
 *     one, so album and single-track music share it (EVE-13640).
 *   - Soundscape / Infinite (ambient music): Artist only.
 *
 * Generic `T` lets callers pass richer objects (e.g. `MediaNarrator`) and
 * receive them back in `members` without losing extra fields.
 */
export function resolveCreditGroups<
    N extends AudioPlayerCredit,
    A extends AudioPlayerCredit
>(
    controls: AudioPlayerControls,
    narrator?: N,
    author?: A
): CreditGroup<N | A>[] {
    const isComposerNarrator = !!narrator?.isComposer;
    const narratorName =
        !isComposerNarrator && narrator?.name ? narrator.name : undefined;
    const artistName =
        isComposerNarrator && narrator?.name ? narrator.name : undefined;
    const authorName = author?.name ? author.name : undefined;

    switch (controls) {
        case AudioPlayerControls.Soundscape:
        case AudioPlayerControls.Infinite: {
            if (artistName && narrator) {
                return [
                    {
                        labelKey: 'common.artist',
                        role: 'narrator',
                        members: [narrator],
                    },
                ];
            }
            return [];
        }
        case AudioPlayerControls.Playlist:
        case AudioPlayerControls.MusicAlbum:
        case AudioPlayerControls.MusicSingle: {
            // Every multi-track collection (music album, sleep-story playlist,
            // DTC course/series) shares credit rules with single-track music:
            // a composer-narrator renders as the Artist credit regardless of
            // track count — billing is a content-type concern, not a nav-style
            // one (EVE-13640).
            // All three roles share a name. Web lacks a combined three-role
            // label, so emit separate blocks (matches mobile fallback intent).
            // Note: artist and narrator are mutually exclusive given the
            // partitioning above, so this branch is logically unreachable
            // with the current single-entity input shape. Kept for parity
            // with Android in case a future shape allows both.
            if (
                narratorName &&
                narratorName === authorName &&
                narratorName === artistName &&
                narrator &&
                author
            ) {
                return [
                    {
                        labelKey: 'common.narrator',
                        role: 'narrator',
                        members: [narrator],
                    },
                    {
                        labelKey: 'common.author',
                        role: 'author',
                        members: [author],
                    },
                    {
                        labelKey: 'common.artist',
                        role: 'narrator',
                        members: [narrator],
                    },
                ];
            }
            // narrator == author, distinct artist (or no artist)
            if (
                narratorName &&
                narratorName === authorName &&
                narratorName !== artistName &&
                narrator &&
                author
            ) {
                return [
                    {
                        labelKey: 'common.narrator_and_author',
                        role: 'narrator',
                        members: [narrator],
                    },
                ];
            }
            // artist == author, distinct narrator (or no narrator)
            if (
                artistName &&
                artistName === authorName &&
                artistName !== narratorName &&
                narrator &&
                author
            ) {
                const groups: CreditGroup<N | A>[] = [
                    {
                        labelKey: 'common.artist_and_author',
                        role: 'narrator',
                        members: [narrator],
                    },
                ];
                if (narratorName) {
                    groups.push({
                        labelKey: 'common.narrator',
                        role: 'narrator',
                        members: [narrator],
                    });
                }
                return groups;
            }
            // narrator == artist, distinct author (or no author).
            // Note: artist and narrator are mutually exclusive given the
            // partitioning above, so this branch is logically unreachable
            // with the current single-entity input shape. Kept for parity
            // with Android in case a future shape allows both.
            if (
                narratorName &&
                narratorName === artistName &&
                narratorName !== authorName &&
                narrator
            ) {
                const groups: CreditGroup<N | A>[] = [
                    {
                        labelKey: 'common.artist',
                        role: 'narrator',
                        members: [narrator],
                    },
                ];
                if (authorName && author) {
                    groups.push({
                        labelKey: 'common.author',
                        role: 'author',
                        members: [author],
                    });
                }
                return groups;
            }
            // All different (or only some present) — emit individually.
            const groups: CreditGroup<N | A>[] = [];
            if (narratorName && narrator)
                groups.push({
                    labelKey: 'common.narrator',
                    role: 'narrator',
                    members: [narrator],
                });
            if (authorName && author)
                groups.push({
                    labelKey: 'common.author',
                    role: 'author',
                    members: [author],
                });
            if (artistName && narrator)
                groups.push({
                    labelKey: 'common.artist',
                    role: 'narrator',
                    members: [narrator],
                });
            return groups;
        }
        default: {
            // Regular: combine Narrator+Author when equal; never show Artist.
            if (narratorName && narratorName === authorName && narrator) {
                return [
                    {
                        labelKey: 'common.narrator_and_author',
                        role: 'narrator',
                        members: [narrator],
                    },
                ];
            }
            const groups: CreditGroup<N | A>[] = [];
            if (narratorName && narrator)
                groups.push({
                    labelKey: 'common.narrator',
                    role: 'narrator',
                    members: [narrator],
                });
            if (authorName && author)
                groups.push({
                    labelKey: 'common.author',
                    role: 'author',
                    members: [author],
                });
            return groups;
        }
    }
}

/**
 * Thin adapter over {@link resolveCreditGroups} that returns the original
 * `{labelKey, names}` shape. Output is byte-for-byte identical to the
 * previous monolithic implementation for every possible input.
 */
export function getCreditBlocks(
    controls: AudioPlayerControls,
    narrator?: AudioPlayerCredit,
    author?: AudioPlayerCredit
): CreditBlock[] {
    return resolveCreditGroups(controls, narrator, author).map((g) => ({
        labelKey: g.labelKey,
        names: g.members.map((m) => m.name).filter((n): n is string => !!n),
    }));
}

export interface AudioPlayerCreditsProps {
    /** Narrator on this track. `isComposer: true` upgrades to Artist credit. */
    narrator?: AudioPlayerCredit;
    /** Author on this track. */
    author?: AudioPlayerCredit;
    /** Player variant — drives label rules. See `getCreditBlocks`. */
    controls?: AudioPlayerControls;
    sx?: SxProps<Theme>;
}

const INTER_BLOCK_SPACING = 14;

/** Renders the credit blocks shown below the title in the full-screen player. */
export function AudioPlayerCredits({
    narrator,
    author,
    controls = AudioPlayerControls.Regular,
    sx,
}: AudioPlayerCreditsProps) {
    const { formatMessage } = useIntl();

    const credits = useMemo(
        () => getCreditBlocks(controls, narrator, author),
        [controls, narrator, author]
    );

    if (credits.length === 0) {
        return null;
    }

    return (
        <Stack
            direction="row"
            alignItems="center"
            spacing={INTER_BLOCK_SPACING}
            sx={sx}
        >
            {credits.map((credit) => (
                <Box key={credit.labelKey}>
                    <Typography
                        variant="body1"
                        color="text.sub"
                        mb={4}
                        textAlign="center"
                    >
                        {formatMessage(
                            commonMessages[credit.labelKey]
                        ).toLocaleUpperCase()}
                    </Typography>
                    {credit.names.map((name, i) => (
                        <Typography
                            variant="subtitle2"
                            color="text.body"
                            textAlign="center"
                            component="div"
                            key={i}
                        >
                            {name}
                            {i < credit.names.length - 1 ? ', ' : ''}
                        </Typography>
                    ))}
                </Box>
            ))}
        </Stack>
    );
}
